// Invoice Processing - Main Listing Page

// Displays invoices with left sidebar filters, search, table, and preview panel
// Based on mockup designs for Invoice Processing System

// react
import { useCallback, useContext, useMemo, useState } from 'react';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useNavigate, useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../../App.context';
import { ContextProject } from '../../Project/Project.context';

// mui
import {
	alpha,
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	Grid,
	IconButton,
	InputAdornment,
	List,
	ListItemButton,
	MenuItem,
	Paper,
	Select,
	Stack,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel,
	Tabs,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
	useTheme,
} from '@mui/material';

// recharts
import {
	Area,
	AreaChart,
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	Legend,
	Pie,
	PieChart,
	ResponsiveContainer,
	Tooltip as RechartsTooltip,
	XAxis,
	YAxis,
} from 'recharts';

// icons
import {
	Add as IconAdd,
	Analytics as IconAnalytics,
	Business as IconVendor,
	Cancel as IconRejected,
	CheckCircle as IconApproved,
	Close as IconClose,
	Receipt as IconInvoice,
	Edit as IconEdit,
	Email as IconEmail,
	Description as IconContract,
	HourglassEmpty as IconPending,
	ListAlt as IconList,
	Payment as IconPayment,
	PictureAsPdf as IconPdf,
	Schedule as IconOverdue,
	Search as IconSearch,
	TrendingUp as IconTrending,
	Visibility as IconView,
	FlagCircle as IconMilestone,
} from '@mui/icons-material';

// components
import { HeaderListingPage } from '../../../ui/HeaderListingPage/HeaderListingPage';

// ui
import { Empty } from '../../../ui/Empty/Empty';

// types
import { V2Contract, Invoice, InvoiceStatus, Voucher } from '../../../../@types';

// context
import { ContextSpendPlan } from '../ProjectV2.context';

// dialogs
import { DialogAddInvoice } from '../../../dialogs/DialogAddInvoice/DialogAddInvoice';
import {
	DialogDocumentViewer,
	ViewerDocumentInfo,
} from '../../../dialogs/DialogDocumentViewer/DialogDocumentViewer';

// services
import { fetchAiDocuments } from '../../../../services/aiApi';

// data
import { ContractDocument } from '../../../../data';

// utils
import { getContractNum } from '../Contracts/Contracts.utils';

// design system
// utils
import { formatCurrency } from '../../../../utils/currency';
import { resolveContractFxRate } from '../../../../utils/fxRate';
import { TruncatedText } from '../../../ui/TruncatedText/TruncatedText';

// Resolve a display name for an invoice's supplier. The supplier is now a
// number id, a populated Entity, or null; only an Entity object carries a name.
const getInvoiceSupplierName = (invoice: Invoice): string =>
	typeof invoice.supplier === 'object' && invoice.supplier ? invoice.supplier.name : '';

// Status Chip Component

const InvoiceStatusChip = ({ status }: { status: InvoiceStatus }) => {
	const theme = useTheme();
	const { t } = useTranslation();

	const statusConfig: Record<
		InvoiceStatus,
		{ label: string; color: string; bgColor: string; icon: React.ReactNode }
	> = {
		PENDING_REVIEW: {
			label: t('spendPlan.invoices.statusPending'),
			color: theme.palette.warning.dark,
			bgColor: alpha(theme.palette.warning.main, 0.1),
			icon: <IconPending sx={{ fontSize: 16 }} />,
		},
		READY_FOR_PAYMENT: {
			label: t('approved'),
			color: theme.palette.success.dark,
			bgColor: alpha(theme.palette.success.main, 0.1),
			icon: <IconApproved sx={{ fontSize: 16 }} />,
		},
		PAYMENT_IN_PROGRESS: {
			label: t('spendPlan.invoices.statusInProgress'),
			color: theme.palette.info.dark,
			bgColor: alpha(theme.palette.info.main, 0.1),
			icon: <IconPayment sx={{ fontSize: 16 }} />,
		},
		PAID: {
			label: t('paid'),
			color: theme.palette.success.dark,
			bgColor: alpha(theme.palette.success.main, 0.1),
			icon: <IconApproved sx={{ fontSize: 16 }} />,
		},
		REJECTED: {
			label: t('rejected'),
			color: theme.palette.error.dark,
			bgColor: alpha(theme.palette.error.main, 0.1),
			icon: <IconRejected sx={{ fontSize: 16 }} />,
		},
		ON_HOLD: {
			label: t('spendPlan.invoices.statusOverdue'),
			color: theme.palette.warning.dark,
			bgColor: alpha(theme.palette.warning.main, 0.15),
			icon: <IconOverdue sx={{ fontSize: 16 }} />,
		},
		DRAFT: {
			label: t('draft'),
			color: theme.palette.grey[600],
			bgColor: alpha(theme.palette.grey[500], 0.1),
			icon: <IconPending sx={{ fontSize: 16 }} />,
		},
		DEACTIVATED: {
			label: 'Deactivated',
			color: theme.palette.grey[600],
			bgColor: alpha(theme.palette.grey[500], 0.1),
			icon: <IconRejected sx={{ fontSize: 16 }} />,
		},
	};

	const config = statusConfig[status];

	return (
		<Chip
			icon={config.icon as React.ReactElement}
			label={config.label}
			size="small"
			sx={{
				color: config.color,
				bgcolor: config.bgColor,
				fontWeight: 600,
				fontSize: '1.35rem',
				height: 28,
				'& .MuiChip-icon': {
					color: config.color,
				},
			}}
		/>
	);
};

// Invoice Preview Panel Component

interface InvoicePreviewPanelProps {
	invoice: Invoice | null;
	onClose: () => void;
	onViewDetails: (id: number) => void;
	onPrepareForPayment: (id: number) => void;
	onApprove: (id: number) => void;
	onReject: (id: number) => void;
	onProcessPayment: (id: number) => void;
	onResumeDraft?: (invoice: Invoice) => void;
	getInvoiceLocal?: (invoice: Invoice, value: number) => string | null;
}

const InvoicePreviewPanel = ({
	invoice,
	onClose,
	onViewDetails,
	onPrepareForPayment,
	onApprove,
	onReject,
	onProcessPayment,
	onResumeDraft,
	getInvoiceLocal,
}: InvoicePreviewPanelProps) => {
	const theme = useTheme();
	const { t } = useTranslation();

	// Document viewer state
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerIndex, setViewerIndex] = useState(0);
	const [viewerDocInfos, setViewerDocInfos] = useState<ViewerDocumentInfo[]>([]);

	const viewerDocuments: ContractDocument[] = useMemo(() => {
		if (!invoice) return [];
		return invoice.attachments.map((att) => ({
			id: String(att.id),
			name: att.name,
			size:
				att.size < 1024 * 1024
					? `${Math.round(att.size / 1024)} KB`
					: `${(att.size / (1024 * 1024)).toFixed(1)} MB`,
			type: (att.name.split('.').pop()?.toLowerCase() || 'doc') as 'doc' | 'pdf' | 'image' | 'xlsx',
			category: 'other' as const,
			uploadedAt: att.uploadedAt,
			uploadedBy: att.uploadedBy,
		}));
	}, [invoice]);

	const viewerBlobFilenames = useMemo(() => {
		if (!invoice) return [];
		return invoice.attachments.map((att) => att.name);
	}, [invoice]);

	const handleViewAttachment = useCallback(
		async (index: number) => {
			if (!invoice) return;
			setViewerIndex(index);
			setViewerOpen(true);

			const att = invoice.attachments[index];
			if (!att) return;

			const initialInfos: ViewerDocumentInfo[] = invoice.attachments.map((a) => ({
				name: a.name,
				size:
					a.size < 1024 * 1024
						? `${Math.round(a.size / 1024)} KB`
						: `${(a.size / (1024 * 1024)).toFixed(1)} MB`,
				uploadedAt: a.uploadedAt,
				uploadedBy: a.uploadedBy,
				sourceType: 'invoice',
				sourceLabel: getInvoiceSupplierName(invoice),
			}));
			setViewerDocInfos(initialInfos);

			try {
				const res = await fetchAiDocuments({ search: att.name });
				const aiDoc =
					res.data?.find((d: { filename?: string }) => d.filename === att.name) || res.data?.[0];
				if (aiDoc?.job_id) {
					setViewerDocInfos((prev) => {
						const updated = [...prev];
						if (updated[index]) {
							updated[index] = {
								...updated[index],
								id: aiDoc.id,
								jobId: aiDoc.job_id || undefined,
							};
						}
						return updated;
					});
				}
			} catch {
				/* silent */
			}
		},
		[invoice]
	);

	// Action buttons based on status
	const renderActionButtons = () => {
		if (!invoice) return null;

		switch (invoice.status) {
			case 'DRAFT':
				if (invoice.aiExtraction) {
					// Verification stage — navigate to detail page
					return (
						<Stack spacing={1.5}>
							<Button
								fullWidth
								variant="contained"
								color="info"
								startIcon={<IconView />}
								onClick={() => onViewDetails?.(invoice.id)}
								sx={{ fontWeight: 600 }}>
								{t('invoices.viewAndVerify', 'View & Verify')}
							</Button>
							<Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
								{t(
									'invoices.aiExtractionComplete',
									'AI extraction complete — verify conditions before payment'
								)}
							</Typography>
						</Stack>
					);
				}
				return (
					<Stack spacing={1.5}>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							startIcon={<IconEdit />}
							onClick={() => onResumeDraft?.(invoice)}
							sx={{ fontWeight: 600 }}>
							{t('invoices.continueEditing', 'Continue Editing')}
						</Button>
						<Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
							{t('invoices.resumeDraftHint', 'Resume the invoice wizard to complete this draft')}
						</Typography>
					</Stack>
				);
			case 'PENDING_REVIEW':
				return (
					<Stack spacing={1.5}>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							startIcon={<IconView />}
							onClick={() => onPrepareForPayment(invoice.id)}
							sx={{ fontWeight: 600 }}>
							{t('invoices.viewDetails')}
						</Button>
						<Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
							{t('invoices.reviewPrepareHint', 'Review invoice and prepare for payment')}
						</Typography>
					</Stack>
				);
			case 'READY_FOR_PAYMENT':
				return (
					<Stack spacing={1.5}>
						<Button
							fullWidth
							variant="contained"
							color="success"
							startIcon={<IconPayment />}
							onClick={() => onProcessPayment(invoice.id)}
							sx={{ fontWeight: 600 }}>
							{t('invoices.processPayment', 'Process Payment')}
						</Button>
						<Stack direction="row" spacing={1}>
							<Button
								fullWidth
								variant="outlined"
								color="error"
								size="small"
								startIcon={<IconRejected />}
								onClick={() => onReject(invoice.id)}
								sx={{ fontWeight: 600 }}>
								{t('reject', 'Reject')}
							</Button>
						</Stack>
					</Stack>
				);
			default:
				return null;
		}
	};

	if (!invoice) {
		return (
			<Paper
				elevation={0}
				sx={{
					width: 440,
					flexShrink: 0,
					borderLeft: '1px solid',
					borderColor: 'divider',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					bgcolor: alpha(theme.palette.grey[100], 0.5),
				}}>
				<Typography color="text.secondary" sx={{ fontSize: '1.35rem' }}>
					{t('spendPlan.invoices.selectToPreview')}
				</Typography>
			</Paper>
		);
	}

	return (
		<Paper
			elevation={0}
			sx={{
				width: 440,
				flexShrink: 0,
				borderLeft: '1px solid',
				borderColor: 'divider',
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}>
			{/* Header */}
			<Box
				sx={{
					p: 2,
					borderBottom: '1px solid',
					borderColor: 'divider',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
				}}>
				<Typography variant="h6" sx={{ fontWeight: 600 }}>
					{t('invoices.preview', 'Invoice Preview')}
				</Typography>
				<Stack direction="row" spacing={1}>
					<Tooltip title={t('invoices.viewDetails')}>
						<IconButton size="small" onClick={() => onViewDetails(invoice.id)}>
							<IconView sx={{ fontSize: 20 }} />
						</IconButton>
					</Tooltip>
					<IconButton size="small">
						<IconEmail sx={{ fontSize: 20 }} />
					</IconButton>
				</Stack>
			</Box>

			{/* Top Action Buttons */}
			{(invoice.status === 'DRAFT' ||
				invoice.status === 'PENDING_REVIEW' ||
				invoice.status === 'READY_FOR_PAYMENT') && (
				<Box
					sx={{
						p: 2,
						borderBottom: '1px solid',
						borderColor: 'divider',
						bgcolor: alpha(theme.palette.primary.main, 0.02),
					}}>
					{renderActionButtons()}
				</Box>
			)}

			{/* Invoice Preview Card */}
			<Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
				{/* Status Badge */}
				<Box sx={{ mb: 2 }}>
					<InvoiceStatusChip status={invoice.status} />
				</Box>

				{/* Vendor Info */}
				<Box
					sx={{
						bgcolor: alpha(theme.palette.primary.main, 0.04),
						borderRadius: 2,
						p: 2,
						mb: 2,
					}}>
					<Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
						{t('vendor')}
					</Typography>
					<Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main', mb: 1 }}>
						{getInvoiceSupplierName(invoice)}
					</Typography>
					<Divider sx={{ my: 1.5 }} />
					<Grid container spacing={2}>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary">
								{t('invoices.invoiceNo', 'Invoice No.')}
							</Typography>
							<Typography variant="body2" fontWeight={600}>
								{invoice.num}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary">
								{t('invoices.invoiceDate', 'Invoice Date')}
							</Typography>
							<Typography variant="body2">
								{invoice.date
									? new Date(invoice.date).toLocaleDateString(undefined, { timeZone: 'UTC' })
									: '—'}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary">
								{t('dueDate')}
							</Typography>
							<Typography variant="body2">
								{invoice.dateDue
									? new Date(invoice.dateDue).toLocaleDateString(undefined, { timeZone: 'UTC' })
									: '—'}
							</Typography>
						</Grid>
						<Grid item xs={6}>
							<Typography variant="caption" color="text.secondary">
								{t('amount')}
							</Typography>
							<Typography variant="body2" fontWeight={700} color="primary.main">
								{formatCurrency({ value: invoice.amount ?? 0, currency: invoice.currency })}
							</Typography>
							{(() => {
								const local = getInvoiceLocal?.(invoice, invoice.amount ?? 0);
								return local ? (
									<Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
										{local}
									</Typography>
								) : null;
							})()}
						</Grid>
					</Grid>
				</Box>

				{/* Contract Info */}
				<Box
					sx={{
						bgcolor: alpha(theme.palette.info.main, 0.04),
						borderRadius: 2,
						p: 2,
						mb: 2,
						border: '1px solid',
						borderColor: alpha(theme.palette.info.main, 0.2),
					}}>
					<Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
						<IconContract sx={{ fontSize: 18, color: theme.palette.info.main }} />
						<Typography variant="subtitle2" color="text.secondary">
							{t('invoices.linkedContract', 'Linked Contract')}
						</Typography>
					</Stack>
					<Typography variant="body2" fontWeight={600} color="info.main" sx={{ mb: 0.5 }}>
						{typeof invoice.contract === 'object' ? invoice.contract?.num : `#${invoice.contract}`}
					</Typography>
					<Typography variant="body2" sx={{ mb: 1 }}>
						{typeof invoice.contract === 'object' ? invoice.contract?.description : ''}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						{t('contractValue')}:{' '}
						{typeof invoice.contract === 'object' && invoice.contract?.amounts?.[0]
							? formatCurrency({
									value: invoice.contract.amounts[0].amount,
									currency: invoice.contract.amounts[0].currency,
								})
							: '—'}
					</Typography>
				</Box>

				{/* Line Items Summary */}
				<Card variant="outlined" sx={{ mb: 2 }}>
					<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
							{t('invoices.lineItems', 'Line Items')} ({invoice.lineItems.length})
						</Typography>
						<Table size="small">
							<TableBody>
								{invoice.lineItems.slice(0, 3).map((item, index) => (
									<TableRow key={index}>
										<TableCell sx={{ fontSize: '1.35rem', py: 0.5, pl: 0, border: 0 }}>
											{index + 1}. {item.description}
										</TableCell>
										<TableCell
											align="right"
											sx={{ fontSize: '1.35rem', py: 0.5, pr: 0, border: 0, fontWeight: 500 }}>
											{formatCurrency({ value: item.total, currency: invoice.currency })}
										</TableCell>
									</TableRow>
								))}
								{invoice.lineItems.length > 3 && (
									<TableRow>
										<TableCell
											colSpan={2}
											sx={{
												fontSize: '1.35rem',
												py: 0.5,
												pl: 0,
												border: 0,
												color: 'text.secondary',
											}}>
											{t('invoices.moreItems', { count: invoice.lineItems.length - 3 })}
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
						<Divider sx={{ my: 1 }} />
						<Stack direction="row" justifyContent="space-between" alignItems="center">
							<Typography variant="body2" fontWeight={600}>
								{t('total')}
							</Typography>
							<Box sx={{ textAlign: 'right' }}>
								<Typography variant="subtitle1" fontWeight={700} color="primary.main">
									{formatCurrency({ value: invoice.amount ?? 0, currency: invoice.currency })}
								</Typography>
								{(() => {
									const local = getInvoiceLocal?.(invoice, invoice.amount ?? 0);
									return local ? (
										<Typography variant="caption" sx={{ display: 'block', color: '#6b7280' }}>
											{local}
										</Typography>
									) : null;
								})()}
							</Box>
						</Stack>
					</CardContent>
				</Card>

				{/* Attachments */}
				{invoice.attachments.length > 0 && (
					<Box sx={{ mb: 2 }}>
						<Typography
							variant="caption"
							color="text.secondary"
							sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
							{t('invoices.attachments', 'Attachments')} ({invoice.attachments.length})
						</Typography>
						<Stack spacing={1}>
							{invoice.attachments.slice(0, 2).map((attachment) => (
								<Box
									key={attachment.id}
									sx={{
										display: 'flex',
										alignItems: 'center',
										gap: 1,
										p: 1,
										bgcolor: alpha(theme.palette.grey[100], 0.8),
										borderRadius: 1,
									}}>
									<IconPdf sx={{ fontSize: 20, color: theme.palette.error.main }} />
									<Box sx={{ flex: 1, minWidth: 0 }}>
										<Typography
											variant="body2"
											noWrap
											onClick={() => {
												const idx = invoice.attachments.findIndex((a) => a.id === attachment.id);
												if (idx >= 0) handleViewAttachment(idx);
											}}
											sx={{
												fontWeight: 500,
												cursor: 'pointer',
												color: 'primary.main',
												'&:hover': { textDecoration: 'underline' },
											}}>
											{attachment.name}
										</Typography>
									</Box>
									<Typography variant="caption" color="text.secondary">
										{Math.round(attachment.size / 1024)} KB
									</Typography>
								</Box>
							))}
							{invoice.attachments.length > 2 && (
								<Typography variant="caption" color="text.secondary">
									{t('invoices.moreAttachments', { count: invoice.attachments.length - 2 })}
								</Typography>
							)}
						</Stack>
					</Box>
				)}
			</Box>

			{/* Bottom Action Buttons */}
			{(invoice.status === 'DRAFT' ||
				invoice.status === 'PENDING_REVIEW' ||
				invoice.status === 'READY_FOR_PAYMENT') && (
				<Box
					sx={{
						p: 2,
						borderTop: '1px solid',
						borderColor: 'divider',
						bgcolor: alpha(theme.palette.grey[100], 0.5),
					}}>
					{renderActionButtons()}
				</Box>
			)}

			{/* Document Viewer Dialog */}
			{viewerDocuments.length > 0 && (
				<DialogDocumentViewer
					isOpen={viewerOpen}
					currentIndex={viewerIndex}
					documents={viewerDocuments}
					blobFilenames={viewerBlobFilenames}
					documentInfos={viewerDocInfos}
					onClose={() => setViewerOpen(false)}
					onNavigate={setViewerIndex}
				/>
			)}
		</Paper>
	);
};

// Main Invoices Component

export const Invoices = () => {
	// hooks
	const { t } = useTranslation();
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const theme = useTheme();

	// context
	const { project } = useContext(ContextProject);
	const { setBanner } = useContext(ContextBanner);
	const { invoices, contracts, conditions, vouchers, workPlans, updateInvoice, refetchInvoices } =
		useContext(ContextSpendPlan);
	// URL params
	const filterStatus = (searchParams.get('status') || 'ALL') as InvoiceStatus | 'ALL' | 'DISPUTED';
	const filterSupplier = searchParams.get('supplier') || 'ALL';
	const searchQuery = searchParams.get('search') || '';

	// state
	const [selectedInvoiceId, setSelectedInvoiceId] = useState<number | null>(null);
	const [tabValue, setTabValue] = useState(0);
	const [contractPickerOpen, setContractPickerOpen] = useState(false);
	const [contractFilterMode, setContractFilterMode] = useState<'milestones' | 'all'>('milestones');
	const [selectedContract, setSelectedContract] = useState<V2Contract | null>(null);
	const [addInvoiceOpen, setAddInvoiceOpen] = useState(false);
	const [resumeDraftInvoice, setResumeDraftInvoice] = useState<Invoice | null>(null);

	// Unique suppliers derived from real data
	const suppliers = useMemo(() => {
		const seen = new Map<string, { id: number | null; name: string }>();
		invoices.forEach((inv) => {
			const suppId = typeof inv.supplier === 'number' ? inv.supplier : (inv.supplier?.id ?? null);
			const name = getInvoiceSupplierName(inv);
			if (!seen.has(name)) {
				seen.set(name, { id: suppId, name });
			}
		});
		return Array.from(seen.values());
	}, [invoices]);

	// Enrich invoices with populated contract objects from context
	const enrichedInvoices = useMemo(() => {
		if (!contracts.length) return invoices;
		const contractMap = new Map(contracts.map((c) => [c.id, c]));
		return invoices.map((inv) => {
			const contractId = typeof inv.contract === 'number' ? inv.contract : inv.contract?.id;
			const contract = contractId != null ? contractMap.get(contractId) : undefined;
			return contract ? { ...inv, contract: contract as Invoice['contract'] } : inv;
		});
	}, [invoices, contracts]);

	// Stats from real data
	const stats = useMemo(() => {
		const pending = invoices.filter((i) => i.status === 'PENDING_REVIEW').length;
		const approved = invoices.filter((i) => i.status === 'READY_FOR_PAYMENT').length;
		const rejected = invoices.filter((i) => i.status === 'REJECTED').length;
		const paid = invoices.filter((i) => i.status === 'PAID').length;
		const overdue = invoices.filter((i) => i.status === 'ON_HOLD').length;
		const totalAmount = invoices.reduce((sum, i) => sum + (i.amount ?? 0), 0);
		const pendingAmount = invoices
			.filter((i) => i.status === 'PENDING_REVIEW')
			.reduce((sum, i) => sum + (i.amount ?? 0), 0);
		return {
			total: invoices.length,
			pending,
			approved,
			rejected,
			paid,
			overdue,
			totalAmount,
			pendingAmount,
			currency: invoices[0]?.currency || 'USD',
		};
	}, [invoices]);

	// Restate an invoice's amount (denominated in the contract currency) into
	// the project's local currency. An invoice carries no captured rate of its
	// own, so we restate via the linked contract's commitment rate
	// (commitmentAmountLocal / commitmentAmount); when the contract carries no
	// local commitment, fall back to the work plan's budget rate so the figure
	// rests on a single, FX-variance-free basis. Returns null when no local
	// currency or rate is available, or when the amount is already in local
	// currency (the equivalent would just duplicate it).
	const getInvoiceLocal = useCallback(
		(invoice: Invoice, value: number): string | null => {
			const contractId =
				typeof invoice.contract === 'object' ? invoice.contract?.id : invoice.contract;
			const contract = contracts.find((c) => c.id === contractId);
			const workPlan = contract ? workPlans.find((wp) => wp.id === contract.workPlan) : undefined;
			const fx = resolveContractFxRate(contract, workPlan, project);
			if (!fx || invoice.currency === fx.localCurrency) return null;
			return formatCurrency({ currency: fx.localCurrency, value: value * fx.rate });
		},
		[contracts, workPlans, project]
	);

	// Filtered invoices (client-side filter on top of enriched data)
	const filteredInvoices = useMemo(() => {
		let result = enrichedInvoices;
		if (filterStatus === 'DISPUTED') {
			result = result.filter((inv) => inv.supplierConfirmationStatus === 'DISPUTED');
		} else if (filterStatus !== 'ALL') {
			result = result.filter((inv) => inv.status === filterStatus);
		}
		if (filterSupplier !== 'ALL') {
			result = result.filter((inv) => {
				const suppId = typeof inv.supplier === 'number' ? inv.supplier : inv.supplier?.id;
				return suppId === Number(filterSupplier) || getInvoiceSupplierName(inv) === filterSupplier;
			});
		}
		if (searchQuery) {
			const q = searchQuery.toLowerCase();
			result = result.filter(
				(inv) =>
					(inv.num || '').toLowerCase().includes(q) ||
					getInvoiceSupplierName(inv).toLowerCase().includes(q) ||
					(inv.description && inv.description.toLowerCase().includes(q))
			);
		}
		return result;
	}, [enrichedInvoices, filterStatus, filterSupplier, searchQuery]);

	// Selected invoice for preview
	const selectedInvoice = useMemo(() => {
		if (!selectedInvoiceId) return null;
		return enrichedInvoices.find((inv) => inv.id === selectedInvoiceId) || null;
	}, [selectedInvoiceId, enrichedInvoices]);

	// Analytics Data
	const monthlyTrendData = useMemo(() => {
		const monthKey = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
		const monthLabel = (d: Date) =>
			d.toLocaleDateString('en-US', {
				month: 'short',
			});

		const now = new Date();
		const months: { key: string; label: string }[] = [];
		for (let i = 5; i >= 0; i -= 1) {
			const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
			months.push({ key: monthKey(d), label: monthLabel(d) });
		}

		const trendMap = new Map<
			string,
			{ month: string; pending: number; approved: number; paid: number }
		>();
		months.forEach((m) => {
			trendMap.set(m.key, { month: m.label, pending: 0, approved: 0, paid: 0 });
		});

		invoices.forEach((invoice) => {
			const sourceDate =
				invoice.date || invoice.dateReceived || invoice.created || invoice.modified || null;
			if (!sourceDate) return;

			const parsed = new Date(sourceDate);
			if (Number.isNaN(parsed.getTime())) return;

			const key = monthKey(new Date(parsed.getFullYear(), parsed.getMonth(), 1));
			const row = trendMap.get(key);
			if (!row) return;

			const amount = invoice.amount ?? 0;
			if (invoice.status === 'PAID') {
				row.paid += amount;
				return;
			}
			if (invoice.status === 'READY_FOR_PAYMENT' || invoice.status === 'PAYMENT_IN_PROGRESS') {
				row.approved += amount;
				return;
			}
			row.pending += amount;
		});

		return months.map(
			(m) => trendMap.get(m.key) || { month: m.label, pending: 0, approved: 0, paid: 0 }
		);
	}, [invoices]);

	const statusDistributionData = useMemo(() => {
		const approvedCount = invoices.filter(
			(i) => i.status === 'READY_FOR_PAYMENT' || i.status === 'PAYMENT_IN_PROGRESS'
		).length;
		const overdueCount = invoices.filter((i) => i.status === 'ON_HOLD').length;
		return [
			{ name: 'Pending', value: stats.pending, color: theme.palette.warning.main },
			{ name: t('approved'), value: approvedCount, color: theme.palette.success.main },
			{ name: 'Rejected', value: stats.rejected, color: theme.palette.error.main },
			{ name: t('paid'), value: stats.paid, color: theme.palette.info.main },
			{ name: 'Overdue', value: overdueCount, color: theme.palette.warning.dark },
		];
	}, [invoices, stats.pending, stats.rejected, stats.paid, theme]);

	const vendorDistributionData = useMemo(() => {
		const vendorCounts: Record<string, { name: string; amount: number; count: number }> = {};
		invoices.forEach((inv) => {
			const name = getInvoiceSupplierName(inv);
			if (!vendorCounts[name]) {
				vendorCounts[name] = { name, amount: 0, count: 0 };
			}
			vendorCounts[name].amount += inv.amount ?? 0;
			vendorCounts[name].count += 1;
		});
		return Object.values(vendorCounts)
			.sort((a, b) => b.amount - a.amount)
			.slice(0, 6);
	}, [invoices]);

	const agingData = useMemo(() => {
		const buckets = [
			{ range: '0-30 days', count: 0, amount: 0 },
			{ range: '31-60 days', count: 0, amount: 0 },
			{ range: '61-90 days', count: 0, amount: 0 },
			{ range: '90+ days', count: 0, amount: 0 },
		];

		const now = new Date();
		invoices.forEach((invoice) => {
			if (invoice.status === 'PAID') return;

			const baseDate = invoice.dateDue || invoice.date || invoice.dateReceived || null;
			if (!baseDate) return;

			const parsed = new Date(baseDate);
			if (Number.isNaN(parsed.getTime())) return;

			const ageDays = Math.max(
				0,
				Math.floor((now.getTime() - parsed.getTime()) / (1000 * 60 * 60 * 24))
			);
			const amount = invoice.amount ?? 0;

			if (ageDays <= 30) {
				buckets[0].count += 1;
				buckets[0].amount += amount;
			} else if (ageDays <= 60) {
				buckets[1].count += 1;
				buckets[1].amount += amount;
			} else if (ageDays <= 90) {
				buckets[2].count += 1;
				buckets[2].amount += amount;
			} else {
				buckets[3].count += 1;
				buckets[3].amount += amount;
			}
		});

		return buckets;
	}, [invoices]);

	// Tab handler
	const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue);
	};

	// Handlers
	const handleSearchChange = (value: string) => {
		setSearchParams((params) => {
			if (value) {
				params.set('search', value);
			} else {
				params.delete('search');
			}
			return params;
		});
	};

	const handleStatusFilter = (status: InvoiceStatus | 'ALL' | 'DISPUTED') => {
		setSearchParams((params) => {
			if (status === 'ALL') {
				params.delete('status');
			} else {
				params.set('status', status);
			}
			return params;
		});
	};

	const handleSupplierFilter = (supplierId: string) => {
		setSearchParams((params) => {
			if (supplierId === 'ALL') {
				params.delete('supplier');
			} else {
				params.set('supplier', supplierId);
			}
			return params;
		});
	};

	const handleResumeDraft = (invoice: Invoice) => {
		const contractId =
			typeof invoice.contract === 'number' ? invoice.contract : invoice.contract?.id;
		const contract = contracts.find((c) => c.id === contractId) || null;
		setSelectedContract(contract);
		setResumeDraftInvoice(invoice);
		setAddInvoiceOpen(true);
	};

	const handleViewInvoice = (id: number) => {
		const invoice = enrichedInvoices.find((inv) => inv.id === id);
		// Upload-stage DRAFTs (no AI extraction) reopen the dialog to complete upload
		if (invoice?.status === 'DRAFT' && !invoice.aiExtraction) {
			handleResumeDraft(invoice);
			return;
		}
		// Verification-stage DRAFTs and all other statuses navigate to the detail page
		navigate(`/project/${project?.idWbg}/spend-plans/invoice/${id}`);
	};

	const handlePrepareForPayment = (id: number) => {
		// Navigate to invoice detail page for payment review
		navigate(`/project/${project?.idWbg}/spend-plans/invoice/${id}`);
	};

	const handleApproveInvoice = (id: number) => {
		// Navigate to invoice detail page for approval
		navigate(`/project/${project?.idWbg}/spend-plans/invoice/${id}`);
	};

	const handleRejectInvoice = async (id: number) => {
		try {
			const res = await updateInvoice(id, { status: 'REJECTED' });
			if (res?.error) {
				setBanner({
					message: `Failed to reject invoice: ${res.error.message}`,
					severity: 'error',
				});
				return;
			}
			setBanner({ message: 'Invoice rejected.', severity: 'success' });
			refetchInvoices();
		} catch (err) {
			console.error('Failed to reject invoice:', err);
			setBanner({
				message: `Failed to reject invoice: ${err instanceof Error ? err.message : 'Unknown error'}`,
				severity: 'error',
			});
		}
	};

	const handleProcessPayment = (id: number) => {
		navigate(`/project/${project?.idWbg}/spend-plans/invoice/${id}?action=payment`);
	};

	const handleAddInvoice = () => {
		setContractPickerOpen(true);
	};

	const handleContractSelected = useCallback((contract: V2Contract) => {
		setSelectedContract(contract);
		setContractPickerOpen(false);
		setAddInvoiceOpen(true);
	}, []);

	// Derive milestones for the selected contract from conditions
	const selectedContractMilestones = useMemo(() => {
		if (!selectedContract) return [];
		return conditions
			.filter((c) => {
				const condContract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
				return condContract === selectedContract.id && c.conditionType === 'payment';
			})
			.map((c) => ({
				id: c.id,
				name: c.name || `Milestone ${c.sequenceOrder || c.id}`,
				percentage: c.paymentPercentage || 0,
				amount:
					c.paymentAmount ||
					(c.paymentPercentage
						? ((selectedContract.amounts?.[0]?.amount || 0) * c.paymentPercentage) / 100
						: 0),
			}));
	}, [conditions, selectedContract]);

	// Build milestone info per contract for the contract picker dialog
	const contractMilestoneInfo = useMemo(() => {
		// Build a set of condition IDs that have a non-cancelled voucher
		const voucherConditionIds = new Set(
			vouchers
				.filter((v: Voucher) => v.condition != null && v.status !== 'CANCELLED')
				.map((v: Voucher) => (typeof v.condition === 'number' ? v.condition : v.condition?.id))
				.filter(Boolean) as number[]
		);

		const infoMap = new Map<
			number,
			{
				totalMilestones: number;
				availableMilestones: { id: number; name: string; hasVoucher: boolean }[];
				availableCount: number;
				allHaveVouchers: boolean;
			}
		>();

		for (const contract of contracts) {
			const milestones = conditions
				.filter((c) => {
					const condContract = typeof c.contract === 'number' ? c.contract : c.contract?.id;
					return condContract === contract.id && c.conditionType === 'payment';
				})
				.map((c) => ({
					id: c.id,
					name: c.name || `Milestone ${c.sequenceOrder || c.id}`,
					hasVoucher: voucherConditionIds.has(c.id),
				}));

			const availableCount = milestones.filter((m) => !m.hasVoucher).length;

			infoMap.set(contract.id, {
				totalMilestones: milestones.length,
				availableMilestones: milestones,
				availableCount,
				allHaveVouchers: milestones.length > 0 && availableCount === 0,
			});
		}

		return infoMap;
	}, [contracts, conditions, vouchers]);

	// Filtered contracts for the picker dialog
	const filteredPickerContracts = useMemo(() => {
		return contracts.filter((c) => {
			const info = contractMilestoneInfo.get(c.id);
			// Always hide contracts where all milestones already have vouchers
			if (info?.allHaveVouchers) return false;
			// In milestones mode, only show contracts that have milestones
			if (contractFilterMode === 'milestones') {
				return (info?.totalMilestones ?? 0) > 0;
			}
			return true;
		});
	}, [contracts, contractMilestoneInfo, contractFilterMode]);

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
				bgcolor: 'background.default',
			}}>
			{/* Header */}
			<HeaderListingPage
				icon={IconInvoice}
				title={t('spendPlan.invoices.processingTitle')}
				subtitle={t('spendPlan.invoices.subtitle')}
				variant="financial"
				stats={[
					{
						label: t('spendPlan.invoices.totalInvoices'),
						value: stats.total,
						subValue: formatCurrency({ value: stats.totalAmount, currency: stats.currency }),
					},
					{
						label: t('spendPlan.invoices.pendingReview'),
						value: stats.pending,
						subValue: formatCurrency({ value: stats.pendingAmount, currency: stats.currency }),
						color: theme.palette.warning.main,
					},
					{
						label: t('approved'),
						value: stats.approved,
						color: theme.palette.success.main,
					},
					{
						label: t('paid'),
						value: stats.paid,
						color: theme.palette.success.main,
					},
				]}
				actions={
					<Button
						variant="contained"
						startIcon={<IconAdd />}
						onClick={handleAddInvoice}
						sx={{
							borderRadius: 3,
							fontWeight: 600,
							px: 3,
							py: 1.25,
							textTransform: 'none',
						}}>
						{t('spendPlan.invoices.addInvoice')}
					</Button>
				}
			/>

			{/* Tabs */}
			<Box
				sx={{
					borderBottom: '1px solid',
					borderColor: 'divider',
					bgcolor: 'background.paper',
				}}>
				<Tabs
					value={tabValue}
					onChange={handleTabChange}
					sx={{
						minHeight: 44,
						'& .MuiTabs-indicator': {
							height: 5,
						},
						'& .MuiTab-root': {
							minHeight: 44,
							py: 0.5,
							fontWeight: 600,
							textTransform: 'none',
							fontSize: '1.5rem',
						},
					}}>
					<Tab
						icon={<IconList sx={{ fontSize: 20 }} />}
						iconPosition="start"
						label={t('spendPlan.invoices.invoiceList')}
					/>
					<Tab
						icon={<IconAnalytics sx={{ fontSize: 20 }} />}
						iconPosition="start"
						label={t('spendPlan.invoices.invoiceAnalytics')}
					/>
				</Tabs>
			</Box>

			{/* Tab Content */}
			{tabValue === 0 && (
				<Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
					{/* Main Content */}
					<Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
						{/* Search and Filters */}
						<Box
							sx={{
								p: 2.5,
								borderBottom: '1px solid',
								borderColor: 'divider',
								bgcolor: 'background.paper',
							}}>
							<Stack direction="row" spacing={2} alignItems="center">
								<TextField
									placeholder={t('spendPlan.invoices.searchPlaceholder')}
									size="small"
									value={searchQuery}
									onChange={(e) => handleSearchChange(e.target.value)}
									sx={{ width: 300 }}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<IconSearch sx={{ fontSize: 20, color: 'text.disabled' }} />
											</InputAdornment>
										),
										endAdornment: searchQuery ? (
											<InputAdornment position="end">
												<IconButton size="small" onClick={() => handleSearchChange('')}>
													<IconClose sx={{ fontSize: 18 }} />
												</IconButton>
											</InputAdornment>
										) : null,
									}}
								/>

								<Select
									size="small"
									value={filterSupplier}
									onChange={(e) => handleSupplierFilter(e.target.value)}
									displayEmpty
									sx={{ minWidth: 160 }}
									startAdornment={
										<InputAdornment position="start">
											<IconVendor sx={{ fontSize: 18, color: 'text.disabled' }} />
										</InputAdornment>
									}>
									<MenuItem value="ALL">{t('spendPlan.invoices.allVendors')}</MenuItem>
									{suppliers.map((supplier) => (
										<MenuItem key={supplier.id} value={String(supplier.id)}>
											{supplier.name}
										</MenuItem>
									))}
								</Select>

								<Select
									size="small"
									value={filterStatus}
									onChange={(e) =>
										handleStatusFilter(e.target.value as InvoiceStatus | 'ALL' | 'DISPUTED')
									}
									displayEmpty
									sx={{ minWidth: 140 }}>
									<MenuItem value="ALL">{t('spendPlan.invoices.allStatus')}</MenuItem>
									<MenuItem value="DRAFT">{t('draft')}</MenuItem>
									<MenuItem value="PENDING_REVIEW">{t('pending')}</MenuItem>
									<MenuItem value="READY_FOR_PAYMENT">{t('approved')}</MenuItem>
									<MenuItem value="REJECTED">{t('rejected')}</MenuItem>
									<MenuItem value="PAID">{t('paid')}</MenuItem>
									<MenuItem value="ON_HOLD">{t('overdue')}</MenuItem>
									<MenuItem value="DEACTIVATED">{t('deactivated')}</MenuItem>
									<MenuItem value="DISPUTED" sx={{ color: 'error.main', fontWeight: 600 }}>
										{t('disputed')}
									</MenuItem>
								</Select>
							</Stack>
						</Box>

						{/* Invoice Table */}
						<TableContainer sx={{ flex: 1, overflow: 'auto' }}>
							<Table stickyHeader size="small">
								<TableHead>
									<TableRow>
										<TableCell sx={{ fontWeight: 600, fontSize: '1.35rem', width: 110 }}>
											<TableSortLabel>{t('spendPlan.invoices.invoiceNo')}</TableSortLabel>
										</TableCell>
										<TableCell sx={{ fontWeight: 600, fontSize: '1.35rem', minWidth: 140 }}>
											<TableSortLabel>{t('vendor')}</TableSortLabel>
										</TableCell>
										<TableCell sx={{ fontWeight: 600, fontSize: '1.35rem', minWidth: 180 }}>
											<TableSortLabel>{t('contract')}</TableSortLabel>
										</TableCell>
										<TableCell
											sx={{ fontWeight: 600, fontSize: '1.35rem', width: 100 }}
											align="right">
											<TableSortLabel>{t('amount')}</TableSortLabel>
										</TableCell>
										<TableCell sx={{ fontWeight: 600, fontSize: '1.35rem', width: 90 }}>
											{t('spendPlan.invoices.dueDate')}
										</TableCell>
										<TableCell sx={{ fontWeight: 600, fontSize: '1.35rem', width: 100 }}>
											{t('status')}
										</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filteredInvoices.map((invoice) => (
										<TableRow
											key={invoice.id}
											hover
											selected={selectedInvoiceId === invoice.id}
											onClick={() => setSelectedInvoiceId(invoice.id)}
											onDoubleClick={() => handleViewInvoice(invoice.id)}
											sx={{
												cursor: 'pointer',
												'&.Mui-selected': {
													bgcolor: alpha(theme.palette.primary.main, 0.08),
												},
												'&.Mui-selected:hover': {
													bgcolor: alpha(theme.palette.primary.main, 0.12),
												},
											}}>
											<TableCell sx={{ py: 1.5 }}>
												<TruncatedText
													variant="body2"
													fontWeight={600}
													color="primary.main"
													text={invoice.num ?? ''}
													sx={{ fontSize: '1.35rem', maxWidth: 180 }}
												/>
											</TableCell>
											<TableCell sx={{ py: 1.5 }}>
												<TruncatedText
													variant="body2"
													text={getInvoiceSupplierName(invoice)}
													sx={{ fontSize: '1.35rem', maxWidth: 200 }}
												/>
											</TableCell>
											<TableCell sx={{ py: 1.5 }}>
												<TruncatedText
													variant="body2"
													fontWeight={500}
													color="text.secondary"
													text={
														typeof invoice.contract === 'object'
															? invoice.contract?.num || ''
															: `#${invoice.contract}`
													}
													sx={{ fontSize: '1.35rem', maxWidth: 200 }}
												/>
												<TruncatedText
													variant="caption"
													color="text.disabled"
													text={
														typeof invoice.contract === 'object'
															? invoice.contract?.description || ''
															: ''
													}
													sx={{ display: 'block', maxWidth: 200 }}
												/>
											</TableCell>
											<TableCell sx={{ py: 1.5 }} align="right">
												<Typography variant="body2" fontWeight={600} sx={{ fontSize: '1.35rem' }}>
													{formatCurrency({
														value: invoice.amount ?? 0,
														currency: invoice.currency,
													})}
												</Typography>
												{(() => {
													const local = getInvoiceLocal(invoice, invoice.amount ?? 0);
													return local ? (
														<Typography
															variant="caption"
															sx={{ display: 'block', color: '#6b7280' }}>
															{local}
														</Typography>
													) : null;
												})()}
											</TableCell>
											<TableCell sx={{ py: 1.5 }}>
												<Typography
													variant="body2"
													color="text.secondary"
													sx={{ fontSize: '1.35rem' }}>
													{invoice.dateDue
														? new Date(invoice.dateDue).toLocaleDateString('en-US', {
																timeZone: 'UTC',
																month: 'short',
																day: 'numeric',
															})
														: '—'}
												</Typography>
											</TableCell>
											<TableCell sx={{ py: 1.5 }}>
												<InvoiceStatusChip status={invoice.status} />
											</TableCell>
										</TableRow>
									))}

									{filteredInvoices.length === 0 && (
										<TableRow>
											<TableCell colSpan={6} sx={{ border: 0 }}>
												<Empty
													IconCircleProps={{ icon: <IconInvoice sx={{ fontSize: 32 }} /> }}
													title={{
														children:
															searchQuery || filterSupplier !== 'ALL' || filterStatus !== 'ALL'
																? t('spendPlan.invoices.noInvoicesFiltered')
																: t('spendPlan.invoices.noInvoices'),
													}}
													content={{
														children:
															searchQuery || filterSupplier !== 'ALL' || filterStatus !== 'ALL'
																? t('invoices.adjustSearch')
																: t('spendPlan.invoices.createToGetStarted'),
													}}
													actions={
														searchQuery || filterSupplier !== 'ALL' || filterStatus !== 'ALL'
															? undefined
															: [
																	{
																		children: t('spendPlan.invoices.createInvoice'),
																		onClick: () => setAddInvoiceOpen(true),
																		variant: 'contained',
																		startIcon: <IconAdd />,
																	},
																]
													}
													sx={{ py: 6 }}
												/>
											</TableCell>
										</TableRow>
									)}
								</TableBody>
							</Table>
						</TableContainer>
					</Box>

					{/* Right Panel - Invoice Preview */}
					<InvoicePreviewPanel
						invoice={selectedInvoice}
						onClose={() => setSelectedInvoiceId(null)}
						onViewDetails={handleViewInvoice}
						onPrepareForPayment={handlePrepareForPayment}
						onApprove={handleApproveInvoice}
						onReject={handleRejectInvoice}
						onProcessPayment={handleProcessPayment}
						onResumeDraft={handleResumeDraft}
						getInvoiceLocal={getInvoiceLocal}
					/>
				</Box>
			)}

			{/* Analytics Tab */}
			{tabValue === 1 && (
				<Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
					{/* Summary Cards */}
					<Grid container spacing={3} sx={{ mb: 3 }}>
						<Grid item xs={12} sm={6} md={3}>
							<Paper
								sx={{
									p: 3,
									borderRadius: 2,
									background: `linear-gradient(135deg, ${alpha(theme.palette.warning.main, 0.1)} 0%, ${alpha(theme.palette.warning.main, 0.05)} 100%)`,
								}}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: 2,
											bgcolor: alpha(theme.palette.warning.main, 0.2),
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}>
										<IconPending sx={{ color: theme.palette.warning.main, fontSize: 28 }} />
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t('spendPlan.invoices.pendingReview')}
										</Typography>
										<Typography variant="h4" fontWeight={700}>
											{stats.pending}
										</Typography>
									</Box>
								</Stack>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<Paper
								sx={{
									p: 3,
									borderRadius: 2,
									background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.1)} 0%, ${alpha(theme.palette.success.main, 0.05)} 100%)`,
								}}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: 2,
											bgcolor: alpha(theme.palette.success.main, 0.2),
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}>
										<IconApproved sx={{ color: theme.palette.success.main, fontSize: 28 }} />
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t('approved')}
										</Typography>
										<Typography variant="h4" fontWeight={700}>
											{stats.approved}
										</Typography>
									</Box>
								</Stack>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<Paper
								sx={{
									p: 3,
									borderRadius: 2,
									background: `linear-gradient(135deg, ${alpha(theme.palette.info.main, 0.1)} 0%, ${alpha(theme.palette.info.main, 0.05)} 100%)`,
								}}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: 2,
											bgcolor: alpha(theme.palette.info.main, 0.2),
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}>
										<IconPayment sx={{ color: theme.palette.info.main, fontSize: 28 }} />
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t('spendPlan.invoices.totalPaid')}
										</Typography>
										<Typography variant="h4" fontWeight={700}>
											{stats.paid}
										</Typography>
									</Box>
								</Stack>
							</Paper>
						</Grid>
						<Grid item xs={12} sm={6} md={3}>
							<Paper
								sx={{
									p: 3,
									borderRadius: 2,
									background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.1)} 0%, ${alpha(theme.palette.error.main, 0.05)} 100%)`,
								}}>
								<Stack direction="row" alignItems="center" spacing={2}>
									<Box
										sx={{
											width: 48,
											height: 48,
											borderRadius: 2,
											bgcolor: alpha(theme.palette.error.main, 0.2),
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
										}}>
										<IconOverdue sx={{ color: theme.palette.error.main, fontSize: 28 }} />
									</Box>
									<Box>
										<Typography variant="body2" color="text.secondary">
											{t('spendPlan.invoices.statusOverdue')}
										</Typography>
										<Typography variant="h4" fontWeight={700}>
											{stats.overdue}
										</Typography>
									</Box>
								</Stack>
							</Paper>
						</Grid>
					</Grid>

					{/* Charts Row 1 */}
					<Grid container spacing={3} sx={{ mb: 3 }}>
						{/* Payment Trend Chart */}
						<Grid item xs={12} lg={8}>
							<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
								<Stack
									direction="row"
									alignItems="center"
									justifyContent="space-between"
									sx={{ mb: 3 }}>
									<Box>
										<Typography variant="h6" fontWeight={600}>
											{t('invoices.trends')}
										</Typography>
										<Typography variant="body2" color="text.secondary">
											{t('invoices.monthlyBreakdown')}
										</Typography>
									</Box>
									<IconTrending sx={{ color: theme.palette.primary.main, fontSize: 28 }} />
								</Stack>
								<ResponsiveContainer width="100%" height={300}>
									<AreaChart data={monthlyTrendData}>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke={alpha(theme.palette.divider, 0.5)}
										/>
										<XAxis
											dataKey="month"
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
										/>
										<YAxis
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
											tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
										/>
										<RechartsTooltip
											formatter={(value: number) =>
												formatCurrency({ value, currency: stats.currency })
											}
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Legend />
										<Area
											type="monotone"
											dataKey="pending"
											name={t('pending')}
											stroke={theme.palette.warning.main}
											fill={alpha(theme.palette.warning.main, 0.2)}
											strokeWidth={2}
										/>
										<Area
											type="monotone"
											dataKey="approved"
											name={t('approved')}
											stroke={theme.palette.success.main}
											fill={alpha(theme.palette.success.main, 0.2)}
											strokeWidth={2}
										/>
										<Area
											type="monotone"
											dataKey="paid"
											name={t('paid')}
											stroke={theme.palette.info.main}
											fill={alpha(theme.palette.info.main, 0.2)}
											strokeWidth={2}
										/>
									</AreaChart>
								</ResponsiveContainer>
							</Paper>
						</Grid>

						{/* Status Distribution */}
						<Grid item xs={12} lg={4}>
							<Paper sx={{ p: 3, borderRadius: 2, height: '100%' }}>
								<Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
									{t('invoices.statusDistribution')}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									{t('invoices.statusDistributionDesc')}
								</Typography>
								<ResponsiveContainer width="100%" height={250}>
									<PieChart>
										<Pie
											data={statusDistributionData}
											cx="50%"
											cy="50%"
											innerRadius={50}
											outerRadius={80}
											paddingAngle={2}
											dataKey="value">
											{statusDistributionData.map((entry, index) => (
												<Cell key={`cell-${index}`} fill={entry.color} />
											))}
										</Pie>
										<RechartsTooltip
											formatter={(value: number, name: string) => [value, name]}
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Legend wrapperStyle={{ fontSize: 12 }} />
									</PieChart>
								</ResponsiveContainer>
							</Paper>
						</Grid>
					</Grid>

					{/* Charts Row 2 */}
					<Grid container spacing={3}>
						{/* Vendor Distribution */}
						<Grid item xs={12} lg={6}>
							<Paper sx={{ p: 3, borderRadius: 2 }}>
								<Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
									{t('invoices.topVendors')}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									{t('invoices.topVendorsDesc')}
								</Typography>
								<ResponsiveContainer width="100%" height={280}>
									<BarChart data={vendorDistributionData} layout="vertical">
										<CartesianGrid
											strokeDasharray="3 3"
											stroke={alpha(theme.palette.divider, 0.5)}
										/>
										<XAxis
											type="number"
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
											tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
										/>
										<YAxis
											type="category"
											dataKey="name"
											tick={{ fill: theme.palette.text.secondary, fontSize: 11 }}
											width={120}
										/>
										<RechartsTooltip
											formatter={(value: number) =>
												formatCurrency({ value, currency: stats.currency })
											}
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Bar
											dataKey="amount"
											name={t('amount')}
											fill={theme.palette.primary.main}
											radius={[0, 4, 4, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</Paper>
						</Grid>

						{/* Aging Analysis */}
						<Grid item xs={12} lg={6}>
							<Paper sx={{ p: 3, borderRadius: 2 }}>
								<Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
									{t('invoices.agingAnalysis')}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
									{t('invoices.agingDescription')}
								</Typography>
								<ResponsiveContainer width="100%" height={280}>
									<BarChart data={agingData}>
										<CartesianGrid
											strokeDasharray="3 3"
											stroke={alpha(theme.palette.divider, 0.5)}
										/>
										<XAxis
											dataKey="range"
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
										/>
										<YAxis
											yAxisId="left"
											orientation="left"
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
										/>
										<YAxis
											yAxisId="right"
											orientation="right"
											tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
											tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
										/>
										<RechartsTooltip
											contentStyle={{
												backgroundColor: theme.palette.background.paper,
												border: `1px solid ${theme.palette.divider}`,
												borderRadius: 8,
											}}
										/>
										<Legend />
										<Bar
											yAxisId="left"
											dataKey="count"
											name="Invoice Count"
											fill={theme.palette.secondary.main}
											radius={[4, 4, 0, 0]}
										/>
										<Bar
											yAxisId="right"
											dataKey="amount"
											name={t('amount')}
											fill={alpha(theme.palette.warning.main, 0.7)}
											radius={[4, 4, 0, 0]}
										/>
									</BarChart>
								</ResponsiveContainer>
							</Paper>
						</Grid>
					</Grid>
				</Box>
			)}

			{/* Contract Picker Dialog */}
			<Dialog
				open={contractPickerOpen}
				onClose={() => setContractPickerOpen(false)}
				maxWidth="md"
				fullWidth>
				<DialogTitle>
					<Stack direction="row" alignItems="center" justifyContent="space-between">
						<Box>
							<Stack direction="row" alignItems="center" spacing={1}>
								<IconContract color="primary" sx={{ fontSize: 28 }} />
								<Typography variant="h6" fontWeight={600} sx={{ fontSize: '1.35rem' }}>
									{t('spendPlan.invoices.selectContract')}
								</Typography>
							</Stack>
							<Typography color="text.secondary" sx={{ mt: 0.5, fontSize: '1.1rem' }}>
								{t('spendPlan.invoices.selectContractHelper')}
							</Typography>
						</Box>
						<ToggleButtonGroup
							value={contractFilterMode}
							exclusive
							onChange={(_, val) => val && setContractFilterMode(val)}
							size="small"
							sx={{ ml: 2 }}>
							<ToggleButton
								value="milestones"
								sx={{ textTransform: 'none', fontSize: '0.85rem', px: 1.5 }}>
								<IconMilestone sx={{ fontSize: 16, mr: 0.5 }} />
								{t('spendPlan.invoices.withMilestones')}
							</ToggleButton>
							<ToggleButton
								value="all"
								sx={{ textTransform: 'none', fontSize: '0.85rem', px: 1.5 }}>
								All
							</ToggleButton>
						</ToggleButtonGroup>
					</Stack>
				</DialogTitle>
				<DialogContent dividers>
					{filteredPickerContracts.length === 0 ? (
						<Typography
							color="text.secondary"
							sx={{ py: 4, textAlign: 'center', fontSize: '1.25rem' }}>
							{contracts.length === 0
								? t('spendPlan.invoices.noContractsAvailable')
								: contractFilterMode === 'milestones'
									? t('spendPlan.invoices.noContractsWithMilestones')
									: t('spendPlan.invoices.noContractsForInvoicing')}
						</Typography>
					) : (
						<List disablePadding sx={{ mx: -3 }}>
							{/* Table Header */}
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									px: 3,
									py: 1,
									borderBottom: '2px solid',
									borderColor: 'divider',
									bgcolor: 'grey.50',
								}}>
								<Box sx={{ width: 40 }} />
								<Typography
									variant="caption"
									fontWeight={700}
									sx={{
										minWidth: 100,
										fontSize: '0.85rem',
										color: 'text.secondary',
										textTransform: 'uppercase',
										letterSpacing: 0.5,
									}}>
									{t('contract')}
								</Typography>
								<Typography
									variant="caption"
									fontWeight={700}
									sx={{
										flex: 1,
										fontSize: '0.85rem',
										color: 'text.secondary',
										textTransform: 'uppercase',
										letterSpacing: 0.5,
										ml: 2,
									}}>
									{t('supplier')}
								</Typography>
								<Typography
									variant="caption"
									fontWeight={700}
									sx={{
										width: 140,
										fontSize: '0.85rem',
										color: 'text.secondary',
										textTransform: 'uppercase',
										letterSpacing: 0.5,
										textAlign: 'center',
									}}>
									{t('spendPlan.contracts.milestones')}
								</Typography>
								<Typography
									variant="caption"
									fontWeight={700}
									sx={{
										fontSize: '0.85rem',
										color: 'text.secondary',
										textTransform: 'uppercase',
										letterSpacing: 0.5,
										mr: 2,
									}}>
									{t('amount')}
								</Typography>
								<Typography
									variant="caption"
									fontWeight={700}
									sx={{
										width: 80,
										fontSize: '0.85rem',
										color: 'text.secondary',
										textTransform: 'uppercase',
										letterSpacing: 0.5,
										textAlign: 'center',
									}}>
									{t('status')}
								</Typography>
							</Box>
							{filteredPickerContracts.map((c) => {
								const msInfo = contractMilestoneInfo.get(c.id);
								return (
									<Tooltip
										key={c.id}
										placement="bottom-start"
										arrow
										title={
											msInfo && msInfo.totalMilestones > 0 ? (
												<Box sx={{ p: 0.5 }}>
													<Typography
														variant="caption"
														fontWeight={600}
														sx={{ mb: 0.5, display: 'block' }}>
														{t('invoices.milestones', {
															available: msInfo.availableCount,
															total: msInfo.totalMilestones,
														})}
													</Typography>
													{msInfo.availableMilestones.map((m) => (
														<Stack key={m.id} direction="row" spacing={0.5} alignItems="center">
															<Box
																sx={{
																	width: 6,
																	height: 6,
																	borderRadius: '50%',
																	bgcolor: m.hasVoucher ? 'text.disabled' : 'success.main',
																}}
															/>
															<Typography
																variant="caption"
																sx={{
																	textDecoration: m.hasVoucher ? 'line-through' : 'none',
																	color: m.hasVoucher ? 'text.disabled' : 'text.primary',
																}}>
																{m.name}
																{m.hasVoucher ? t('invoices.voucherIssued') : ''}
															</Typography>
														</Stack>
													))}
												</Box>
											) : (
												''
											)
										}>
										<ListItemButton
											onClick={() => handleContractSelected(c)}
											sx={{
												py: 1.5,
												px: 3,
												borderBottom: '1px solid',
												borderColor: 'divider',
												'&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
											}}>
											<Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
												<IconContract sx={{ color: theme.palette.primary.main, fontSize: 24 }} />
												<Box sx={{ minWidth: 100 }}>
													<Typography fontWeight={600} sx={{ fontSize: '1.25rem' }}>
														{getContractNum(c)}
													</Typography>
													{c.description && (
														<Typography
															color="text.secondary"
															noWrap
															sx={{ fontSize: '1.1rem', maxWidth: 200 }}>
															{c.description}
														</Typography>
													)}
												</Box>
												<Typography color="text.secondary" sx={{ fontSize: '1.15rem', flex: 1 }}>
													{c.entities?.find(
														(e: { type?: string; name?: string }) => e.type === 'SUPPLIER'
													)?.name || t('invoices.noSupplier')}
												</Typography>
												{/* Milestones column */}
												<Box sx={{ width: 140, textAlign: 'center' }}>
													{msInfo && msInfo.totalMilestones > 0 ? (
														<Chip
															icon={<IconMilestone sx={{ fontSize: 14 }} />}
															label={t('spendPlan.invoices.milestoneAvail', {
																count: msInfo.availableCount,
																total: msInfo.totalMilestones,
															})}
															size="small"
															color={msInfo.availableCount > 0 ? 'success' : 'default'}
															variant="outlined"
															sx={{ height: 24, fontSize: '0.8rem' }}
														/>
													) : (
														<Typography
															variant="caption"
															color="text.disabled"
															sx={{ fontSize: '0.85rem' }}>
															None
														</Typography>
													)}
												</Box>
												{c.amounts?.[0] && (
													<Typography fontWeight={500} sx={{ fontSize: '1.15rem' }}>
														{formatCurrency({
															value: c.amounts[0].amount,
															currency: c.amounts[0].currency,
														})}
													</Typography>
												)}
												<Box sx={{ width: 80, display: 'flex', justifyContent: 'center' }}>
													<Chip
														label={c.status}
														size="small"
														sx={{ height: 24, fontSize: '0.85rem' }}
													/>
												</Box>
											</Stack>
										</ListItemButton>
									</Tooltip>
								);
							})}
						</List>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setContractPickerOpen(false)} sx={{ fontSize: '1.25rem' }}>
						{t('cancel')}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Add Invoice Dialog */}
			<DialogAddInvoice
				isOpen={addInvoiceOpen}
				onClose={() => {
					setAddInvoiceOpen(false);
					setSelectedContract(null);
					setResumeDraftInvoice(null);
				}}
				onSuccess={() => {
					setAddInvoiceOpen(false);
					setSelectedContract(null);
					setResumeDraftInvoice(null);
					refetchInvoices();
				}}
				contractId={selectedContract?.id ?? null}
				contractNum={selectedContract ? getContractNum(selectedContract) : ''}
				supplierName={
					selectedContract?.entities?.find(
						(e: { type?: string; name?: string }) => e.type === 'SUPPLIER'
					)?.name || t('na')
				}
				contractCurrency={selectedContract?.amounts?.[0]?.currency || 'USD'}
				contractAmount={selectedContract?.amounts?.[0]?.amount || 0}
				milestones={selectedContractMilestones}
				projectId={project?.id}
				draftInvoice={resumeDraftInvoice}
			/>
		</Box>
	);
};
